// Direct request contracts (REQ-*, bead hf-e6-commitments-coverage-udt.1).
//
// "Could you specifically do this?" — one organiser, one named person, one need.
//
// ── THE SHAPE IS THE INVARIANT ─────────────────────────────────────────────────
//
// A Direct Request contributes ZERO coverage until it is accepted (invariant 3,
// REQ-002, REQ-003, COV-003). Nothing in this file carries a capacity, a count or a
// coverage status, and that absence is load-bearing rather than an omission: a wire
// type with no number on it cannot be added up by a client that got clever. The one
// place a count appears is `RespondToRequestResult`, and only on the ACCEPT branch —
// where a Commitment has just been created and the count is the commitment's.
//
// It is a separate module from `./commitments.js` for the same reason the domain
// model keeps the two apart: a request is a question and a commitment is a promise,
// they have different lifecycles, and a file that held both would eventually grow a
// helper that treated one as a weaker version of the other.
//
// ── WHY THERE IS NO `expiresAt` INPUT ──────────────────────────────────────────
//
// Expiry is circle policy, applied server-side (REQ-009). A client that could choose
// the deadline could choose one far enough away that "expires" never happens, which
// is the failure the requirement exists to prevent — an unanswered ask holding a
// place open forever while the need quietly goes uncovered.

import { z } from "zod";

import type { RequestRejection, Result } from "../errors.js";
import type { CommitmentAccepted, WireWindow } from "./commitments.js";
import {
  circleIdSchema,
  directRequestIdSchema,
  personIdSchema,
  revisionSchema,
  supportNeedIdSchema,
} from "../primitives.js";

/**
 * Ask ONE named person (REQ-001, AVL-026).
 *
 * `requestedPersonId` is singular and there is no array anywhere in this module.
 * The could-help surface offers no bulk action, and the wire shape is why that is a
 * property of the system rather than a property of one screen: "ask everyone" is not
 * expressible, so it cannot be added by a client, a script, or a future agent who
 * thought it would be convenient.
 *
 * `needRevision` is the version the ORGANISER was looking at. It travels onto the
 * request row, so that if the need changes materially underneath, the ask is
 * superseded rather than silently repointed at different work (REQ-008).
 */
export const sendRequestInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  requestedPersonId: personIdSchema,
  needRevision: revisionSchema,
  /** Retry-safe: a double tap on a train must not send the same person two asks. */
  idempotencyKey: z.string().min(8).max(128),
});

/**
 * The recipient's answer (REQ-004, REQ-005).
 *
 * TWO VALUES, and neither of them is "maybe" or "seen". A third would let a request
 * sit in a state that reads as progress without anybody having decided anything,
 * which is exactly the false reassurance REQ-003 forbids — and an organiser chasing
 * a "maybe" is chasing nothing.
 *
 * `needRevision` is sent back with the answer so the server can refuse an acceptance
 * of a version that moved while the person was reading it.
 */
export const respondToRequestInput = z.object({
  circleId: circleIdSchema,
  directRequestId: directRequestIdSchema,
  needRevision: revisionSchema,
  /** A direct request contributes ZERO coverage until this says "accept" (invariant 3). */
  response: z.enum(["accept", "decline"]),
  idempotencyKey: z.string().min(8).max(128),
});

/**
 * The organiser takes the question back (REQ-010).
 *
 * No reason field, in either direction. Withdrawing an ask is an organiser tidying
 * up — the need got covered, they changed their mind about whom to ask — and making
 * them justify it would put a sentence about a real person into an audit row for no
 * operational gain (§16.18).
 */
export const withdrawRequestInput = z.object({
  circleId: circleIdSchema,
  directRequestId: directRequestIdSchema,
});

export const requestsForNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
});

export type SendRequestInput = z.input<typeof sendRequestInput>;
export type RespondToRequestInput = z.input<typeof respondToRequestInput>;
export type WithdrawRequestInput = z.input<typeof withdrawRequestInput>;
export type RequestsForNeedInput = z.input<typeof requestsForNeedInput>;

/**
 * One ask, as the person who was asked needs to see it.
 *
 * SELF-CONTAINED ON PURPOSE. Somebody deciding whether they can drive on Tuesday
 * must not have to open the need, cross-reference the calendar, and come back. The
 * window, the label and who asked are all here, which is also what lets the helper
 * home (`hf-e6-commitments-coverage-udt.9`) render a decision without a second read.
 *
 * NO COVERAGE FIELDS. Not the need's status, not its confirmed count. A person
 * answering an ask is answering a question about themselves; showing them "1 of 2
 * confirmed" beside it would turn a request into a nudge about how badly they are
 * needed, which §10.1's tone rules and the product's no-pressure stance both refuse.
 */
export interface PendingRequestView {
  readonly directRequestId: string;
  readonly supportNeedId: string;
  readonly label: string;
  readonly window: WireWindow;
  /** What a Yes will bind to. Sent back with the answer (REQ-006). */
  readonly needRevision: number;
  /** Who asked, by chosen name. The ask is from a person, never from "the system". */
  readonly requestedByName: string;
  readonly askedAt: number;
  /**
   * When this ask stops standing (REQ-009).
   *
   * Carried so the helper can be told the ask has a horizon without the client
   * having to know the circle's expiry policy — and so a client can stop showing an
   * ask the moment it lapses, rather than waiting for a job to notice.
   */
  readonly expiresAt: number;
  /** Server clock used for this read, anchoring the browser's monotonic deadline tick. */
  readonly evaluatedAt: number;
}

/** A person, named, for the organiser's three lists. */
export interface RequestParticipant {
  readonly personId: string;
  readonly displayName: string;
  readonly directRequestId: string;
  /** Persisted deadline; the browser may only hide this server-open row after it. */
  readonly expiresAt: number;
}

/**
 * The effective circle policy shown at the point an organiser sends an ask.
 *
 * This is returned by the server rather than copied into the browser. The client may
 * choose the words around it, but it cannot guess which policy is active (REQ-009).
 */
export interface RequestExpiryPolicyView {
  readonly unansweredTtlDays: number;
}

/**
 * The state of every ask on one need, for the person who sent them (REQ-011).
 *
 * FOUR LISTS FROM ONE FACT — the request's own state, with expiry derived at read
 * time rather than trusted from the row:
 *
 *   awaiting  — `pending`, and not yet past its `expiresAt`. The question is open.
 *   lapsed    — nobody answered in time. THE REASON THIS TYPE EXISTS: an ask that
 *               quietly died is how a need gets stranded, so the organiser is told
 *               and offered the next move rather than left waiting on silence.
 *   declined  — they said no. Recorded plainly; no-fault is a copy decision, and the
 *               copy lives in `@herefor/product-language`.
 *   accepted  — they said yes. The COMMITMENT is what covers the need from then on;
 *               this list exists so the organiser can see the ask reached its end,
 *               not so anything counts it.
 *
 * NOTHING IS COUNTED HERE. `convex/coverage/read.ts` decides the need's status and
 * this decides nothing at all — the same arrangement `RecheckAudienceView` has, and
 * for the same reason: two tallies of one fact eventually disagree.
 */
export interface NeedRequestsView {
  readonly supportNeedId: string;
  /** Server clock used to classify all four lists in this snapshot. */
  readonly evaluatedAt: number;
  readonly expiryPolicy: RequestExpiryPolicyView;
  readonly awaiting: readonly RequestParticipant[];
  readonly lapsed: readonly RequestParticipant[];
  readonly declined: readonly RequestParticipant[];
  readonly accepted: readonly RequestParticipant[];
}

/**
 * Sending succeeded. Deliberately thin.
 *
 * There is no coverage in this result because sending an ask changes no coverage —
 * the return type is the invariant restated at the one moment a client is most
 * tempted to optimistically update a chip.
 */
export interface RequestSent {
  readonly directRequestId: string;
  readonly boundNeedRevision: number;
  readonly expiresAt: number;
}

/**
 * Answering succeeded.
 *
 * `null` is a DECLINE: nothing was created, and there is nothing to report. An
 * accept carries the commitment the claim guard made, counts and all — because at
 * that moment the coverage really did move, and the person deserves to see it.
 */
export type RespondToRequestResult = Result<CommitmentAccepted | null, RequestRejection>;

export type SendRequestResult = Result<RequestSent, RequestRejection>;
export type WithdrawRequestResult = Result<{ directRequestId: string }, RequestRejection>;
export type MyRequestsResult = Result<readonly PendingRequestView[], RequestRejection>;
export type NeedRequestsResult = Result<NeedRequestsView, RequestRejection>;
